/**
 * Controls for one Risk setting. The control type, options and range all come
 * from the server catalogue (RiskSettingsService.Catalog) — nothing is guessed
 * here. Values travel as the strings SystemSettings stores.
 */

import type { RiskSettingOption, RiskSettingRow } from '@/services/riskSettingsApi';

/** matrix value → row → option (unknown / unparseable → empty). */
export function parseMatrix(value: string | null | undefined): Record<string, string> {
  if (!value) return {};
  try {
    const o = JSON.parse(value) as unknown;
    if (!o || typeof o !== 'object' || Array.isArray(o)) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) if (typeof v === 'string') out[k] = v;
    return out;
  } catch {
    return {};
  }
}

export function displayValue(row: RiskSettingRow, value: string | null | undefined): string {
  if (value == null) return '(not set)';
  switch (row.control) {
    case 'toggle':
      return value === 'true' ? 'On' : 'Off';
    case 'select':
      return row.options?.find((o) => o.value === value)?.label ?? value;
    case 'multi': {
      const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
      if (parts.length === 0) return 'None';
      return parts.map((p) => row.options?.find((o) => o.value === p)?.label ?? p).join(', ');
    }
    case 'number':
      return row.unit ? `${value} ${row.unit}` : value;
    case 'matrix': {
      const m = parseMatrix(value);
      return (row.rows ?? [])
        .map((r) => `${r.label}: ${row.options?.find((o) => o.value === m[r.value])?.label ?? m[r.value] ?? '—'}`)
        .join('; ');
    }
    default:
      return value;
  }
}

/** Plain-language problem with a draft value, or null. Mirrors the server's checks. */
export function validateDraft(row: RiskSettingRow, value: string): string | null {
  if (row.control === 'number') {
    if (!/^-?\d+$/.test(value.trim())) return 'Enter a whole number.';
    const n = Number.parseInt(value, 10);
    if (row.min != null && n < row.min) return `Must be ${row.min} or more.`;
    if (row.max != null && n > row.max) return `Must be ${row.max} or less.`;
  }
  return null;
}

interface ControlProps {
  readonly row: RiskSettingRow;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (v: string) => void;
}

export function SettingControl({ row, value, disabled, onChange }: ControlProps): JSX.Element {
  const id = `rs-${row.settingKey.replace(/[^a-zA-Z0-9]/g, '-')}`;
  switch (row.control) {
    case 'toggle':
      return (
        <label className="rs-switch" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            role="switch"
            aria-checked={value === 'true'}
            checked={value === 'true'}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          />
          <span className="rs-switch__track" aria-hidden="true" />
          <span>{value === 'true' ? 'On' : 'Off'}</span>
        </label>
      );
    case 'select':
      return (
        <select id={id} className="rs-select" value={value} disabled={disabled} aria-label={row.label}
          onChange={(e) => onChange(e.target.value)}>
          {!(row.options ?? []).some((o) => o.value === value) && (
            <option value={value} disabled>(saved value not recognised: {value || 'blank'})</option>
          )}
          {(row.options ?? []).map((o: RiskSettingOption) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    case 'number': {
      const problem = validateDraft(row, value);
      return (
        <div>
          <div className="rs-number-wrap">
            <input
              id={id}
              type="number"
              inputMode="numeric"
              className="rs-number"
              min={row.min ?? undefined}
              max={row.max ?? undefined}
              step={1}
              value={value}
              disabled={disabled}
              aria-label={row.label}
              aria-invalid={problem != null}
              onChange={(e) => onChange(e.target.value)}
            />
            {row.unit && <span>{row.unit}</span>}
          </div>
          <div className="rs-option-hint">Allowed: {row.min} to {row.max}{row.unit ? ` ${row.unit}` : ''}</div>
          {problem && <div className="rs-row__warn" role="alert">{problem}</div>}
        </div>
      );
    }
    case 'multi': {
      const selected = value.split(',').map((s) => s.trim()).filter(Boolean);
      return (
        <fieldset className="rs-multi" style={{ border: 0, padding: 0, margin: 0 }} aria-label={row.label}>
          {(row.options ?? []).map((o) => (
            <label key={o.value}>
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                disabled={disabled}
                onChange={(e) => {
                  // Keep the catalogue's option order so the stored CSV is stable.
                  const next = (row.options ?? [])
                    .map((x) => x.value)
                    .filter((v) => (v === o.value ? e.target.checked : selected.includes(v)));
                  onChange(next.join(','));
                }}
              />
              {o.label}
            </label>
          ))}
        </fieldset>
      );
    }
    case 'matrix': {
      // One row per choice, each with its own dropdown; saved as one JSON object, rows in
      // catalogue order so the stored value is stable (Bill 2026-10-15).
      const m = parseMatrix(value);
      const first = row.options?.[0]?.value ?? '';
      const write = (key: string, v: string): void => {
        const next: Record<string, string> = {};
        for (const r of row.rows ?? []) next[r.value] = r.value === key ? v : (m[r.value] ?? first);
        onChange(JSON.stringify(next));
      };
      return (
        <table className="rs-matrix" aria-label={row.label}>
          <tbody>
            {(row.rows ?? []).map((r) => (
              <tr key={r.value}>
                <th scope="row" style={{ textAlign: 'left', fontWeight: 500, paddingRight: 12 }}>{r.label}</th>
                <td>
                  <select
                    className="rs-select"
                    value={m[r.value] ?? first}
                    disabled={disabled}
                    aria-label={`${row.label}: ${r.label}`}
                    onChange={(e) => write(r.value, e.target.value)}
                  >
                    {(row.options ?? []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    default:
      return <span className="rs-mono">{value}</span>;
  }
}
